import { formatK } from "../lib/format.js";

/** Renders the outline produced by the vfa-pitch Edge Function. */
export default function PitchOutline({ pitch, onClose }) {
  if (!pitch) return null;

  if (pitch.error) {
    return (
      <div
        style={{
          padding: "10px 14px",
          borderRadius: 8,
          background: "rgba(239,68,68,0.05)",
          border: "1px solid rgba(239,68,68,0.1)",
          color: "var(--red)",
          fontSize: 13,
          marginBottom: 16,
        }}
      >
        {pitch.error}
      </div>
    );
  }

  const points = pitch.proof_points?.points || [];
  const objections = pitch.proof_points?.objections || [];
  const firstStep = pitch.proof_points?.first_step;

  const copyAll = () => {
    const text =
      `${pitch.title}\n\n${pitch.summary || ""}\n\n` +
      (pitch.sections || []).map((s) => `${s.heading}\n${s.body}`).join("\n\n");
    navigator.clipboard.writeText(text);
  };

  return (
    <div
      className="a"
      style={{
        background: "var(--s1)",
        border: "1px solid rgba(59,130,246,0.16)",
        borderRadius: 16,
        padding: "20px 22px",
        marginBottom: 24,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 16,
          marginBottom: 14,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "var(--blue)",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              marginBottom: 6,
            }}
          >
            Pitch outline — {pitch.company}
          </div>
          <div
            style={{
              fontFamily: "var(--d)",
              fontSize: 20,
              fontWeight: 700,
              letterSpacing: "-0.02em",
              marginBottom: 4,
            }}
          >
            {pitch.title}
          </div>
          {pitch.angle && (
            <div style={{ fontSize: 13, color: "var(--t2)" }}>{pitch.angle}</div>
          )}
        </div>
        <div style={{ display: "flex", gap: 6, flexShrink: 0, alignItems: "flex-start" }}>
          {(pitch.est_value_min || pitch.est_value_max) && (
            <div style={{ textAlign: "right", marginRight: 6 }}>
              <div
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  color: "var(--t3)",
                  textTransform: "uppercase",
                }}
              >
                Est. value
              </div>
              <div
                style={{
                  fontFamily: "var(--d)",
                  fontSize: 16,
                  fontWeight: 700,
                  color: "var(--volt)",
                }}
              >
                {formatK(pitch.est_value_min || 0)}–{formatK(pitch.est_value_max || 0)}
              </div>
            </div>
          )}
          <SmallButton onClick={copyAll}>Copy</SmallButton>
          <SmallButton onClick={onClose}>Close</SmallButton>
        </div>
      </div>

      {pitch.summary && (
        <div
          style={{
            fontSize: 14,
            color: "var(--t2)",
            lineHeight: 1.7,
            padding: "14px 16px",
            background: "var(--s2)",
            borderRadius: 9,
            borderLeft: "2px solid var(--blue)",
            marginBottom: 16,
          }}
        >
          {pitch.summary}
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 10,
          marginBottom: 16,
        }}
      >
        {(pitch.sections || []).map((s, i) => (
          <div
            key={i}
            style={{
              padding: "14px 16px",
              borderRadius: 10,
              background: "var(--s2)",
              border: "1px solid var(--brd)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 6 }}>
              <span style={{ fontFamily: "var(--d)", fontSize: 12, fontWeight: 700, color: "var(--t3)" }}>
                {String(i + 1).padStart(2, "0")}
              </span>
              <span style={{ fontFamily: "var(--d)", fontSize: 14, fontWeight: 600 }}>
                {s.heading}
              </span>
            </div>
            <div style={{ fontSize: 13, color: "var(--t2)", lineHeight: 1.55 }}>{s.body}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {points.length > 0 && (
          <div>
            <SectionLabel>Talking points</SectionLabel>
            {points.map((t, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  gap: 8,
                  fontSize: 13,
                  color: "var(--t2)",
                  lineHeight: 1.5,
                  marginBottom: 6,
                }}
              >
                <div
                  style={{
                    width: 5,
                    height: 5,
                    borderRadius: "50%",
                    background: "var(--green)",
                    marginTop: 7,
                    flexShrink: 0,
                  }}
                />
                {t}
              </div>
            ))}
          </div>
        )}
        {objections.length > 0 && (
          <div>
            <SectionLabel>Likely objections</SectionLabel>
            {objections.map((o, i) => (
              <div key={i} style={{ marginBottom: 8, fontSize: 13, lineHeight: 1.5 }}>
                <div style={{ color: "var(--t1)", fontWeight: 500 }}>{o.objection}</div>
                <div style={{ color: "var(--t3)" }}>{o.response}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {firstStep && (
        <div
          style={{
            marginTop: 14,
            paddingTop: 14,
            borderTop: "1px solid var(--brd)",
            fontSize: 13,
            color: "var(--t2)",
          }}
        >
          <strong style={{ color: "var(--t1)" }}>Propose first:</strong> {firstStep}
        </div>
      )}
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div
      style={{
        fontSize: 10,
        fontWeight: 700,
        color: "var(--t3)",
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        marginBottom: 8,
      }}
    >
      {children}
    </div>
  );
}

function SmallButton({ children, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "7px 14px",
        borderRadius: 7,
        border: "1px solid var(--brd)",
        background: "transparent",
        color: "var(--t2)",
        fontSize: 12,
        cursor: "pointer",
        height: 32,
      }}
    >
      {children}
    </button>
  );
}
